/**
 * Story 50.6 — which pages the Contentsquare tag may never see.
 *
 * The rule is per DOCUMENT, not per page view, because of three things the live tag does
 * (its `CS_CONF`, fetched 2026-10-11):
 *   - "Tracking URL changes" is on: it sends a pageview for every `pushState`, `replaceState`
 *     and `popstate`, so any URL a document ever shows — including one reached with Back — is
 *     reported once the tag is in that document;
 *   - its error logger sends `location.origin + location.pathname`;
 *   - its first pageview carries `document.referrer`.
 * Contentsquare has no documented way to pause collection for a route after it has loaded, so
 * the only safe boundary is: the tag is never in a document that shows, or has shown, a
 * tainting page. `ContentsquareService` enforces that; `analyticsBoundaryGuard` turns an in-app
 * navigation into such a page into a full page load when the tag is already present.
 *
 * Pure functions, no Angular imports: the guard and the service both depend on this file.
 */

/**
 * First path segments that taint a document. `apply`, `status`, `transfer` and `resubmit`
 * carry a bearer token in the PATH (`/status/:token`); `sa` shows other people's data to
 * staff; `dev` routes exist only in builds without a tag ID and are listed so that stays true
 * if that ever changes. A route-tree spec (`app/app.routes.analytics-boundary.spec.ts`) fails
 * when a new secret-carrying or `sa/` route is not covered here and guarded.
 */
export const TAINTING_FIRST_SEGMENTS: ReadonlySet<string> = new Set([
  'apply',
  'status',
  'transfer',
  'resubmit',
  'sa',
  'dev',
]);

/**
 * Excluded only while it is the current URL. The OIDC callback carries `?code=&state=`, but
 * `angular-oauth2-oidc`'s `tryLogin()` replaces its history entry with a stripped URL before the
 * app leaves it, so it does not taint the document (epic-50-notes C.2).
 */
const CALLBACK_SEGMENTS = ['authentication', 'callback'] as const;

/**
 * Path segments of a router URL or `location.pathname`: query and fragment dropped, empty
 * segments skipped, Angular matrix parameters (`;key=value`) dropped, percent-encoding decoded
 * (the router decodes it before matching, so `/st%61tus/x` reaches `status/:token`), lower case
 * (an odd-cased variant must not slip past just because the router would not match it).
 */
export function pathSegments(url: string): string[] {
  const path = url.split(/[?#]/, 1)[0] ?? '';
  return path
    .split('/')
    .filter((segment) => segment.length > 0)
    .map((raw) => {
      const segment = raw.split(';', 1)[0] ?? '';
      try {
        return decodeURIComponent(segment).toLowerCase();
      } catch {
        return segment.toLowerCase();
      }
    });
}

/** True for a page whose display taints the whole document for analytics. */
export function taintsDocument(url: string): boolean {
  const first = pathSegments(url)[0];
  return first !== undefined && TAINTING_FIRST_SEGMENTS.has(first);
}

/** True for the OIDC callback page. */
export function isOidcCallback(url: string): boolean {
  const [first, second] = pathSegments(url);
  return first === CALLBACK_SEGMENTS[0] && second === CALLBACK_SEGMENTS[1];
}

/**
 * True when a query string still holds the authorization response. `tryLogin()` strips it as soon
 * as it runs; it stays only if sign-in failed before that (for example the provider's
 * configuration could not be loaded), and then the callback DOES taint the document.
 */
export function carriesOidcSecret(search: string): boolean {
  return /[?&](code|state)=/.test(search);
}

/** True while `url` is the current page: the tag must not be injected now. */
export function excludedWhileShown(url: string): boolean {
  return taintsDocument(url) || isOidcCallback(url);
}
